/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import { v4 as uuidv4 } from "uuid";

export interface AllocationItem {
  costumeValueId: string;
  amount: number;
}

export interface SplitMapItem {
  costumeId: string;
  payOrder: string;
  amount?: number;
  allocations: AllocationItem[];
  discountCode?: string;
}

export interface BuildCashPayPayloadInput {
  amount?: number | string;
  currency?: string;
  mode?: string;
  notes?: string;
  payOrder?: string;
  costumeId?: string;
  groupId?: string | null;
  isMultiplePurchase?: boolean;
  allocations?: any[];
  splitMap?: any[];
  boxes?: any[];
  txId?: string;
}

/**
 * Formats allocations array ensuring numeric values for amount and valid costumeValueId
 */
export function formatAllocations(allocations: any[]): AllocationItem[] {
  if (!Array.isArray(allocations)) return [];
  return allocations.map((item: any) => ({
    costumeValueId: item?.costumeValueId || item?.id || "",
    amount: parseFloat(item?.amount) || 0,
  }));
}

/**
 * Builds CreateCashInput payload for CreateCashPayTx mutation.
 * If groupId is present OR isMultiplePurchase is true, uses the unified splitMap payload.
 * Otherwise, uses the standard legacy payload structure.
 */
export function buildCashPayPayload(input: BuildCashPayPayloadInput): any {
  const isGroupOrMultiple = Boolean(input.groupId || input.isMultiplePurchase || (input.splitMap && input.splitMap.length > 0));

  const mode = input.mode || "Cash";
  const currency = input.currency || "TTD";
  const txId = input.txId || `CASH-OFFLINE-TX-${uuidv4().substring(0, 8)}`;

  if (isGroupOrMultiple) {
    let rawSplitMap: SplitMapItem[] = [];

    if (Array.isArray(input.splitMap) && input.splitMap.length > 0) {
      rawSplitMap = input.splitMap.map((entry: any) => {
        const formattedAlloc = formatAllocations(entry.allocations || []);
        const boxAmount = formattedAlloc.length > 0
          ? formattedAlloc.reduce((sum, a) => sum + a.amount, 0)
          : (parseFloat(entry.amount) || 0);

        let costumeId = entry.costumeId || entry.costumeID || input.costumeId || "";
        if (costumeId === "Default") costumeId = (input.costumeId && input.costumeId !== "Default") ? input.costumeId : "";

        let payOrder = entry.payOrder?.id || entry.payOrder || input.payOrder || costumeId;
        if (payOrder === "Default") payOrder = (input.payOrder && input.payOrder !== "Default") ? input.payOrder : (costumeId !== "Default" ? costumeId : "");

        return {
          costumeId: costumeId,
          payOrder: payOrder,
          amount: boxAmount,
          allocations: formattedAlloc,
          ...(entry.discountCode && { discountCode: entry.discountCode }),
        };
      });
    } else if (Array.isArray(input.boxes) && input.boxes.length > 0) {
      rawSplitMap = input.boxes.map((box: any) => {
        const formattedAlloc = formatAllocations(box.allocations || []);
        const boxAmount = formattedAlloc.length > 0
          ? formattedAlloc.reduce((sum, a) => sum + a.amount, 0)
          : (parseFloat(box.amount) || 0);

        return {
          costumeId: box.costumeId || box.costumeID || "",
          payOrder: box.payOrder?.id || box.payOrder || "",
          amount: boxAmount,
          allocations: formattedAlloc,
        };
      });
    } else {
      // Single box with groupId or isMultiplePurchase enabled
      const formattedAlloc = formatAllocations(input.allocations || []);
      const boxAmount = formattedAlloc.length > 0
        ? formattedAlloc.reduce((sum, a) => sum + a.amount, 0)
        : (parseFloat(input.amount as string) || 0);

      rawSplitMap = [
        {
          costumeId: input.costumeId || "",
          payOrder: input.payOrder || "",
          amount: boxAmount,
          allocations: formattedAlloc,
        },
      ];
    }

    const totalAmount = rawSplitMap.reduce((sum, box) => sum + (box.amount || 0), 0);

    return {
      txId,
      amount: totalAmount,
      currency,
      mode,
      splitMap: rawSplitMap,
      ...(input.notes && { notes: input.notes }),
    };
  }

  // Standard single box payment (legacy)
  const legacyAmount = parseFloat(input.amount as string) || 0;
  return {
    amount: legacyAmount,
    currency,
    mode,
    payOrder: input.payOrder,
    txId: input.txId || uuidv4(),
    ...(input.notes && { notes: input.notes }),
    ...(input.allocations && { allocations: input.allocations }),
  };
}
